import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';

import store from './reducers';

import Header from './components/Header/Header';
import API from './API/API';

import MaterialStep from './steps/MaterialStep/MaterialStep';
import ToolStep from './steps/ToolStep/ToolStep';
import InitialStep from './steps/InitialStep/InitialStep';
import CalculateStep from './steps/CalculateStep/CalculateStep';
import MachineStep from './steps/MachineStep/MachineStep';
import Sidebar from './components/Sidebar/Sidebar';
import { useAppDispatch, useAppSelector } from './reducers/hooks';


import { reset as materialReset } from './reducers/materialReducer';
import { reset as machineReset } from './reducers/machineReducer';
import { reset as toolReset, setToolType } from './reducers/toolReducer';

export const Api = new API();

function reducer(state: number, action: any) {
  let newState = typeof action === 'number' ? action : state;

  switch (action) {
    case '+':
      newState++;
      break;
    case '-':
      newState--;
      break;
    case 'restart':
      newState = 0;
      break;
  }

  sessionStorage.setItem('advisor-step', newState.toString());
  return newState;
}

// Solution to get around Provider scope (Putting the Provider in index.tsx errors)
function ToolHandler({step, toolType, setToolType, setToolStepLoaded} : {step: number, toolType: string, setToolType: any, setToolStepLoaded: any}) {
  const [materialGroup, tool] = useAppSelector((state) => [state.material.materialGroup, state.tool]);
  const [prevMaterialGroup, setPrevMaterialGroup] = useState(-1);
  const dispatch = useAppDispatch();

  // Reset tool upon picking a different tool type
  useEffect(() => {
    if ((toolType === 'endmills' && tool.type === 'drill') || (toolType === 'drills' && tool.type === 'endmill')) {
      dispatch(toolReset());
      // Override tool type (so the proper machining parameter is shown in the sidebar)
      dispatch(setToolType(toolType === 'endmills' ? 'endmill' : 'drill'));
    }
    sessionStorage.setItem('advisor-toolType', toolType);
  }, [toolType, dispatch, tool])

  // Reset if going back to step 0
  useEffect(() => {
    if (step === 0) {
      dispatch(materialReset());
      dispatch(machineReset());
      dispatch(toolReset());
      setToolType('');
    }
  }, [step, dispatch])

  // Reset tools if changing material group
  useEffect(() => {
    if (prevMaterialGroup !== materialGroup) {
      // Prevent resetting the tool on first page load
      if (prevMaterialGroup !== -1) {
        dispatch(toolReset());
        setToolStepLoaded(false);
      }
      setPrevMaterialGroup(materialGroup);
    }
  }, [materialGroup, dispatch])

  return <></>;
}

function App() {
  const [step, dispatchStep] = React.useReducer(reducer, parseInt(sessionStorage.getItem('advisor-step') || '0'));
  const [toolType, setToolType] = React.useState(sessionStorage.getItem('advisor-toolType') || '');
  const [toolStepLoaded, setToolStepLoaded] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  useEffect(() => {
    if (step >= 3) {
      setToolStepLoaded(true);
    }
  }, [step])

  return (
    <Provider store={store}>
      <ToolHandler step={step} toolType={toolType} setToolType={setToolType} setToolStepLoaded={setToolStepLoaded} />
      <Header step={step} dispatchStep={dispatchStep} toolType={toolType} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <main>
        <Sidebar step={step} dispatchStep={dispatchStep} toolType={toolType} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        {/* 0 */ step === 0 && <InitialStep dispatchStep={dispatchStep} />}
        {/* 1 */ step >= 1 && <MaterialStep step={step} dispatchStep={dispatchStep} />}
        {/* 2 */ step === 2 && <MachineStep dispatchStep={dispatchStep} setToolType={setToolType} toolType={toolType} />}
        {/* 3 */ step >= 1 && toolStepLoaded &&
          <ToolStep step={step} dispatchStep={dispatchStep} toolType={toolType}>
            {/* 4 */ step === 4 && <CalculateStep dispatchStep={dispatchStep} />}
          </ToolStep>
        }
      </main>
    </Provider>
  );
}

export default App;
